import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";

const empty = { name: "", slug: "", description: "", image: "", order: 99, active: true };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.put(`/admin/categories/${editing.id}`, editing);
      else await api.post("/admin/categories", editing);
      toast.success("Categoria salva");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir "${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    load();
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl tracking-tight">Categorias</h1>
        <Button onClick={() => setEditing({ ...empty })} className="bg-gold text-black hover:bg-gold-light" data-testid="new-category-button">
          <Plus size={15} className="mr-1.5" /> Nova categoria
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="border border-white/10 rounded-lg overflow-hidden bg-[#111111]" data-testid={`category-card-${c.slug}`}>
            {c.image && <img src={c.image} alt="" className="w-full h-36 object-cover" loading="lazy" />}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="font-display text-lg">{c.name}</p>
                <span className="text-[10px] text-zinc-600 font-mono">/{c.slug}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{c.description}</p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="border-white/15 text-zinc-300 hover:text-gold" onClick={() => setEditing({ ...c })} data-testid="edit-category-button">
                  <Pencil size={13} className="mr-1" /> Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => remove(c)} data-testid="delete-category-button">
                  <Trash2 size={13} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111111] border-white/10" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Editar categoria" : "Nova categoria"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Nome *</Label>
                <Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="category-name-input"
                  className="mt-1.5 bg-[#141414] border-white/10" />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto" data-testid="category-slug-input"
                  className="mt-1.5 bg-[#141414] border-white/10" />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="category-description-input"
                  className="mt-1.5 bg-[#141414] border-white/10" />
              </div>
              <div>
                <Label>Imagem (URL)</Label>
                <Input value={editing.image || ""} onChange={(e) => setEditing({ ...editing, image: e.target.value })} data-testid="category-image-input"
                  className="mt-1.5 bg-[#141414] border-white/10" />
              </div>
              <Button type="submit" className="w-full bg-gold text-black hover:bg-gold-light" data-testid="save-category-button">Salvar</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
